'use client';

import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { cloneElement, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ToasterProps } from 'react-hot-toast';
import { Toaster, toast } from 'react-hot-toast';
import { cx } from '../../cx';
import type { ToastMessageProps } from './toast-message';
import { ToastMessage } from './toast-message';
type ToastMessageConfig = Pick<ToastMessageProps, 'type' | 'variantConfig'> & {
  message: ReactNode;
  actionLabel?: string;
  actionOnClick?: () => void;
};
interface Toast {
  (message: ToastMessageConfig, options?: ToasterProps): void;
  custom: (component: ReactElement<any>, options?: ToasterProps) => void;
  success: VoidFunction;
}
type ToasterConfig = ToasterProps & {
  defaultConfig: boolean;
};
const ToastProviderContext = createContext<Toast | null>(null);
const DEFAULT_CONFIG = {
  defaultConfig: true,
  position: 'bottom-right',
  toastOptions: {
    duration: 4000
  },
  containerClassName: 'absolute !left-6 !right-6 sm:!right-16 !bottom-6 sm:!bottom-8'
} as const;
export const ToastProvider = ({
  children
}: PropsWithChildren) => {
  const [config, setConfig] = useState<ToasterConfig>(DEFAULT_CONFIG);
  const lastToastId = useRef<string | null>(null);
  const value = useMemo(() => {
    const updateConfig = (options?: ToasterProps) => {
      const updated = mergeConfig(config, options);
      if (updated) setConfig(updated);
    };
    const toastFn = ({
      message,
      ...props
    }: ToastMessageConfig, options_0?: ToasterProps) => {
      updateConfig(options_0);
      if (lastToastId.current) {
        toast.dismiss(lastToastId.current);
        lastToastId.current = null;
      }
      const toastId = toast.custom(toastState => <ToastMessage {...props} visible={toastState.visible}>
          {message}
        </ToastMessage>);
      lastToastId.current = toastId;
    };
    toastFn.custom = (component: ReactElement<any>, options_1?: ToasterProps) => {
      updateConfig(options_1);
      if (lastToastId.current) {
        toast.dismiss(lastToastId.current);
        lastToastId.current = null;
      }
      const toastId_0 = toast.custom(toastState_0 => cloneElement(component, {
        visible: toastState_0.visible
      }));
      lastToastId.current = toastId_0;
    };
    toastFn.success = () => {
      toastFn({
        type: 'success',
        message: 'Speichern erfolgreich'
      });
    };
    return toastFn;
  }, [config]);
  useEffect(() => {
    const handleClick = () => {
      if (lastToastId.current) {
        toast.dismiss(lastToastId.current);
      }
      lastToastId.current = null;
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);
  return <ToastProviderContext.Provider value={value}>
      {children}
      <div className="fluid-container translate-0 pointer-events-none fixed inset-x-0 bottom-10 z-toast h-0">
        <Toaster {...config} />
      </div>
    </ToastProviderContext.Provider>;
};
export const useToast = () => {
  const toastContext = useContext(ToastProviderContext);
  if (!toastContext) {
    throw new Error("useToast has to be used within <ToastProvider />");
  }
  return toastContext;
};
const mergeConfig = (config: ToasterConfig, options?: ToasterProps): ToasterConfig | undefined => {
  if (!options) {
    if (!config.defaultConfig) return DEFAULT_CONFIG;
    return;
  }
  return {
    ...config,
    ...options,
    defaultConfig: false,
    containerClassName: cx(config.containerClassName, options.containerClassName),
    containerStyle: {
      ...config.containerStyle,
      ...options.containerStyle
    },
    toastOptions: {
      ...config.toastOptions,
      ...options.toastOptions
    }
  };
};